import {
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
} from 'antd'
import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import {
  BUYBACK_FIELD as FIELD,
  BuybackDebtItem,
} from '../../../common/buybackTypes'
import {
  computeDebtFees,
  inputNumberFormatter,
} from '../../utils/buybackHelpers'

export const BuybackFormDebtsListFragment: FunctionComponent = () => {
  const form = Form.useFormInstance()
  const debtsRaw = Form.useWatch(FIELD.Debts) as BuybackDebtItem[] | undefined

  const debtsSignature = useMemo(() => {
    const list = debtsRaw ?? []
    if (!list.length) return ''
    return JSON.stringify(
      list.map((debt, idx) => [
        debt?.[FIELD.DebtId] ?? idx,
        debt?.[FIELD.DebtAmount] ?? 0,
        !!debt?.[FIELD.Secured],
      ])
    )
  }, [debtsRaw])

  const fees = useMemo(() => {
    if (!debtsSignature) return [] as number[]
    try {
      const inputs = JSON.parse(debtsSignature) as [unknown, number, boolean][]
      return computeDebtFees(
        inputs.map(([, amount, secured]) => ({ amount, secured }))
      )
    } catch {
      return [] as number[]
    }
  }, [debtsSignature])

  const totalFees = useMemo(
    () => fees.reduce((sum, fee) => sum + (Number(fee) || 0), 0),
    [fees]
  )

  useEffect(() => {
    if (fees.length === 0) {
      const baseDebtFeeTotal = form.getFieldValue(FIELD.BaseDebtFeeTotal)
      if (baseDebtFeeTotal !== 0) {
        form.setFieldValue(FIELD.BaseDebtFeeTotal, 0)
      }
      return
    }

    const list = (form.getFieldValue(FIELD.Debts) || []) as BuybackDebtItem[]
    for (let i = 0; i < list.length; i++) {
      const currentFee = list[i]?.[FIELD.DebtFee]
      const nextFee = fees[i]
      if (currentFee !== nextFee) {
        form.setFieldValue([FIELD.Debts, i, FIELD.DebtFee], nextFee)
      }
    }

    const baseDebtFeeTotal = form.getFieldValue(FIELD.BaseDebtFeeTotal)
    if (baseDebtFeeTotal !== totalFees) {
      form.setFieldValue(FIELD.BaseDebtFeeTotal, totalFees)
    }
  }, [debtsSignature, fees, totalFees, form])

  return (
    <>
      <Row gutter={[8, 0]} align="top" className="mb-2">
        <Col xs={24} md={8}>
          Popis závazku
        </Col>
        <Col xs={20} md={5}>
          Výše závazku
        </Col>
        <Col xs={4} md={3} className="text-center">
          Zajištění
        </Col>
        <Col xs={20} md={5}>
          Odměna za vyřízení
        </Col>
      </Row>

      <Form.List name={FIELD.Debts}>
        {(fields, { add, remove, move }) => (
          <>
            {fields.map((field, index) => (
              <div key={field.key}>
                <Row gutter={[8, 0]} align="top">
                  <Col xs={24} md={8} xl={8}>
                    <Form.Item
                      name={[field.name, FIELD.DebtName]}
                      rules={[{ required: true, message: 'Zadejte název' }]}
                      className="!mb-4"
                    >
                      <Input placeholder="Název závazku" autoFocus />
                    </Form.Item>
                  </Col>

                  <Col xs={20} md={5} xl={5}>
                    <Form.Item
                      name={[field.name, FIELD.DebtAmount]}
                      rules={[{ required: true, message: 'Zadejte částku' }]}
                      className="!mb-4"
                    >
                      <InputNumber
                        min={0}
                        step={1000}
                        className="w-full"
                        formatter={inputNumberFormatter}
                        addonAfter="Kč"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={4} md={3} xl={3}>
                    <Form.Item
                      name={[field.name, FIELD.Secured]}
                      className="!mb-4 text-center"
                      valuePropName="checked"
                    >
                      <Switch checkedChildren="Ano" unCheckedChildren="Ne" />
                    </Form.Item>
                  </Col>

                  <Col xs={20} md={4} xl={5}>
                    <Form.Item
                      name={[field.name, FIELD.DebtFee]}
                      className="!mb-4 !bg-gray-50"
                    >
                      <InputNumber
                        disabled
                        className="w-full"
                        formatter={inputNumberFormatter}
                        addonAfter="Kč"
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={4} md={4} xl={3} className="!flex gap-2">
                    <Space.Compact className="flex">
                      <Button
                        onClick={() => move(index, index - 1)}
                        disabled={index === 0}
                        icon={<UpOutlined />}
                      />
                      <Button
                        onClick={() => move(index, index + 1)}
                        disabled={index === fields.length - 1}
                        icon={<DownOutlined />}
                      />
                    </Space.Compact>
                    <Button
                      danger
                      className="flex-1"
                      onClick={() => remove(field.name)}
                      disabled={fields.length <= 1}
                      icon={<DeleteOutlined />}
                    />
                  </Col>

                  <Form.Item name={[field.name, FIELD.DebtId]} hidden>
                    <Input />
                  </Form.Item>
                </Row>

                {index !== fields.length - 1 && (
                  <Divider className="md:!hidden !mt-0 !text-gray-300">
                    &
                  </Divider>
                )}
              </div>
            ))}

            <Row gutter={[8, 16]} align="middle" justify="end">
              <Col xs={4} md={4} xl={3}>
                <Button
                  type="primary"
                  ghost
                  block
                  onClick={() => {
                    add({
                      id: uuidv4(),
                      name: '',
                      amount: 0,
                      secured: false,
                    })
                  }}
                  icon={<PlusOutlined />}
                />
              </Col>
            </Row>
          </>
        )}
      </Form.List>
    </>
  )
}
